import React, { useEffect, useRef, useState } from "react";
import { normalizeStatus } from "./utils";

export const Toast = ({ toast, onDismiss }) => {
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(onDismiss, 3200);
        return () => clearTimeout(t);
    }, [toast, onDismiss]);

    if (!toast) return null;
    const isError = toast.type === "error";
    return (
        <div className="fixed bottom-5 right-5 z-[100] max-w-sm">
            <div className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-lg border ${isError ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                <span>{toast.message}</span>
            </div>
        </div>
    );
};

export const ReasonDialog = ({ open, title, confirmLabel, onCancel, onConfirm }) => {
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (open) setReason("");
    }, [open]);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 p-5" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
                <h3 className="text-sm font-bold text-slate-800 mb-3">{title}</h3>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Reason</label>
                <textarea
                    autoFocus
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (optional)"
                    rows={3}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm border border-slate-200 text-slate-600">Cancel</button>
                    <button
                        onClick={() => onConfirm(reason)}
                        className="px-4 py-2 rounded-lg text-sm text-white"
                        style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const StatusBadge = ({ status }) => {
    const key = normalizeStatus(status);
    const map = {
        active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        inactive: "bg-rose-50 text-rose-700 border border-rose-200",
        deactivated: "bg-rose-50 text-rose-700 border border-rose-200",
        pending: "bg-amber-50 text-amber-700 border border-amber-200",
        expired: "bg-gray-50 text-gray-700 border border-gray-200",
        done: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        rejected: "bg-rose-50 text-rose-700 border border-rose-200",
        mixed: "bg-indigo-50 text-indigo-700 border border-indigo-200",
    };
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[key] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            {status || "unknown"}
        </span>
    );
};

export const StatCard = ({ label, value, colorClass, dotClass, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass} ${onClick ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
    >
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </button>
);

// Consistent labeled field wrapper so every form input has a visible label.
export const Field = ({ label, required, children, className = "", hint }) => (
    <div className={`flex flex-col gap-1 ${className}`}>
        <label className="text-xs font-semibold text-slate-500">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        {children}
        {hint && <span className="text-[11px] text-slate-400 mt-0.5">{hint}</span>}
    </div>
);

export const inputClass = "bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm w-full";

/**
 * SearchableSelect — a lightweight, dependency-free searchable dropdown.
 * Used for: Subscription (1-12 months), Sell Person (name + id), Region.
 *
 * options: [{ label, value }]
 */
export const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Search…",
    allowCustom = false,
    className = "",
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);

    const selected = options.find((o) => String(o.value) === String(value));

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase())
    );

    const commitCustom = () => {
        if (allowCustom && query.trim()) {
            onChange(query.trim());
            setOpen(false);
            setQuery("");
        }
    };

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
                className={`${inputClass} flex items-center justify-between text-left disabled:opacity-60`}
            >
                <span className={selected || value ? "text-slate-700" : "text-slate-400"}>
                    {selected ? selected.label : (value || placeholder)}
                </span>
                <span className="text-slate-400 ml-2">▾</span>
            </button>

            {open && !disabled && (
                <div className="absolute z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto">
                    <div className="p-1.5 sticky top-0 bg-white border-b border-slate-100">
                        <input
                            autoFocus
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && commitCustom()}
                            placeholder={placeholder}
                            className="w-full bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs"
                        />
                    </div>
                    {filtered.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-400">
                            {allowCustom ? "No match — press Enter to use this value" : "No matches"}
                        </div>
                    ) : (
                        filtered.map((o) => (
                            <button
                                type="button"
                                key={o.value}
                                onClick={() => {
                                    onChange(o.value);
                                    setOpen(false);
                                    setQuery("");
                                }}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${String(o.value) === String(value) ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700"}`}
                            >
                                {o.label}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
